import { isNil } from "lodash";
import {
  BenchStepKind,
  BenchStopRole,
  type BenchStepSelectorInput,
} from "../../generated/graphql";

/** A step as the report names it: the kind, then the stop role when it has one. */
export const stepLabel = (
  kind: string,
  stopRole: string | null | undefined,
): string => (isNil(stopRole) ? kind : `${kind} (${stopRole})`);

/** One lifecycle step the operator can leave out of a run. */
export type BenchStepOption = {
  /** Matches the backend's `benchStepKey`: kind and stop role. */
  key: string;
  kind: BenchStepKind;
  stopRole: BenchStopRole | null;
  label: string;
  /** What the step can send. */
  sends: string;
  /** The step's place in the run, from 1. */
  order: number;
};

const option = (
  kind: BenchStepKind,
  stopRole: BenchStopRole | null,
  label: string,
  sends: string,
): Omit<BenchStepOption, "order"> => ({
  key: `${kind}:${stopRole ?? ""}`,
  kind,
  stopRole,
  label,
  sends,
});

/**
 * Every lifecycle step, in the order a run takes them, with the 990, 214 or
 * 210 each can send, each status prefixed with its transaction set. A 214 goes out only when the partnership subscribes to
 * it. Line-haul steps cover every manifest the order is on.
 */
const STEPS_IN_RUN_ORDER: ReadonlyArray<Omit<BenchStepOption, "order">> = [
  option(
    BenchStepKind.AcceptTender,
    null,
    "Accept tender",
    "990 ACCEPTED for a draft, or for a live order with the 990 option on, unless the partnership auto-accepts",
  ),
  option(
    BenchStepKind.ConfirmAppointment,
    BenchStopRole.Inbound,
    "Confirm pickup appointment",
    "214 PICKUP_APPOINTMENT_CONFIRMED",
  ),
  option(
    BenchStepKind.RouteStop,
    BenchStopRole.Inbound,
    "Route pickup",
    "Nothing; puts the pickup on a route for the test driver",
  ),
  option(
    BenchStepKind.StartRoute,
    BenchStopRole.Inbound,
    "Start pickup route",
    "214 PICKUP_DISPATCHED",
  ),
  option(
    BenchStepKind.DispatchStop,
    BenchStopRole.Inbound,
    "Dispatch pickup",
    "214 PICKUP_DISPATCHED, only when the route start is left out",
  ),
  option(
    BenchStepKind.ArriveAtStop,
    BenchStopRole.Inbound,
    "Arrive at pickup",
    "214 PICKUP_ARRIVED",
  ),
  option(
    BenchStepKind.ReceivedAtOrigin,
    null,
    "Received at origin",
    "214 ORDER_RECEIVED_AT_ORIGIN",
  ),
  option(
    BenchStepKind.CompleteStop,
    BenchStopRole.Inbound,
    "Complete pickup",
    "214 PICKUP_COMPLETED or 214 RECOVERY_COMPLETED",
  ),
  option(
    BenchStepKind.LineHaulDepart,
    null,
    "Line-haul depart",
    "214 LINE_HAUL_DEPARTED_ORIGIN",
  ),
  option(
    BenchStepKind.LineHaulArrive,
    null,
    "Line-haul arrive",
    "214 LINE_HAUL_ARRIVED_AT_DESTINATION, 214 DELIVERY_ON_HAND",
  ),
  option(
    BenchStepKind.MarkOnHand,
    null,
    "Mark on hand",
    "214 DELIVERY_ON_HAND",
  ),
  option(
    BenchStepKind.ConfirmAppointment,
    BenchStopRole.Outbound,
    "Confirm delivery appointment",
    "214 DELIVERY_APPOINTMENT_CONFIRMED",
  ),
  option(
    BenchStepKind.RouteStop,
    BenchStopRole.Outbound,
    "Route delivery",
    "Nothing; puts the delivery on a route for the test driver",
  ),
  option(
    BenchStepKind.StartRoute,
    BenchStopRole.Outbound,
    "Start delivery route",
    "214 DELIVERY_DISPATCHED",
  ),
  option(
    BenchStepKind.DispatchStop,
    BenchStopRole.Outbound,
    "Dispatch delivery",
    "214 DELIVERY_DISPATCHED, only when the route start is left out",
  ),
  option(
    BenchStepKind.ArriveAtStop,
    BenchStopRole.Outbound,
    "Arrive at delivery",
    "214 DELIVERY_ARRIVED",
  ),
  option(
    BenchStepKind.CompleteStop,
    BenchStopRole.Outbound,
    "Complete delivery",
    "214 DELIVERY_COMPLETED, 214 PICKUP_TRANSFERRED",
  ),
  option(BenchStepKind.FinalizeOrder, null, "Finalize order", "Nothing"),
  option(BenchStepKind.AddToInvoice, null, "Add to invoice", "Nothing"),
  option(BenchStepKind.PostInvoice, null, "Post invoice", "Nothing"),
  option(BenchStepKind.Send_210, null, "Send 210", "210"),
];

export const BENCH_STEP_OPTIONS: readonly BenchStepOption[] =
  STEPS_IN_RUN_ORDER.map((step, index) => ({ ...step, order: index + 1 }));

/** The columns the step table sorts by. */
export type BenchStepSortColumn = "order" | "label" | "stop" | "sends";

export type BenchStepSortDirection = "asc" | "desc";

const STOP_ROLE_LABELS: Record<BenchStopRole, string> = {
  [BenchStopRole.Inbound]: "Pickup",
  [BenchStopRole.Outbound]: "Delivery",
};

/** The stop a step works on; order-level steps have none. */
export const stepStopLabel = (stopRole: BenchStopRole | null): string =>
  stopRole === null ? "Order" : STOP_ROLE_LABELS[stopRole];

const compareBy = (
  column: BenchStepSortColumn,
  a: BenchStepOption,
  b: BenchStepOption,
): number => {
  switch (column) {
    case "order": {
      return a.order - b.order;
    }
    case "label": {
      return a.label.localeCompare(b.label);
    }
    case "stop": {
      return stepStopLabel(a.stopRole).localeCompare(stepStopLabel(b.stopRole));
    }
    case "sends": {
      return a.sends.localeCompare(b.sends);
    }
  }
};

/**
 * The steps sorted by one column. Ties fall back to run order, so steps of
 * the same stop or with the same message still read in the order a run takes
 * them.
 */
export const sortStepOptions = (
  steps: readonly BenchStepOption[],
  column: BenchStepSortColumn,
  direction: BenchStepSortDirection,
): BenchStepOption[] => {
  const sign = direction === "asc" ? 1 : -1;
  return [...steps].sort((a, b) => {
    const byColumn = sign * compareBy(column, a, b);
    return byColumn === 0 ? a.order - b.order : byColumn;
  });
};

/** The `skippedSteps` input for the steps the operator switched off. */
export const skippedStepSelectors = (
  skippedKeys: ReadonlySet<string>,
): BenchStepSelectorInput[] =>
  BENCH_STEP_OPTIONS.filter((step) => skippedKeys.has(step.key)).map(
    ({ kind, stopRole }) => ({ kind, stopRole }),
  );

/** A route step left out, which makes that stop's arrival fail if it has no route. */
export const skipsRouting = (skippedKeys: ReadonlySet<string>): boolean =>
  BENCH_STEP_OPTIONS.some(
    (step) =>
      step.kind === BenchStepKind.RouteStop && skippedKeys.has(step.key),
  );
